import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Save, Send, AlertCircle, CheckCircle, Plane, MapPin, DollarSign,
  Megaphone, Users, Wrench, Banknote, Zap, Heart, Clock, Shield,
  SlidersHorizontal
} from "lucide-react";

import FleetSection from "./FleetSection";
import RoutesSection from "./RoutesSection";
import PricingSection from "./PricingSection";
import MarketingSection from "./MarketingSection";
import HRSection from "./HRSection";
import OperationsSection from "./OperationsSection";
import FinancingSection from "./FinancingSection";
import StrategicSection from "./StrategicSection";
import CustomerExperienceSection from "./CustomerExperienceSection";
import NetworkSection from "./NetworkSection";
import RiskSection from "./RiskSection";
import EventsSection from "./EventsSection";
import WhatIfSimulator from "./WhatIfSimulator";

function parseApiError(error, fallback) {
  const detail = error.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg || JSON.stringify(d)).join("; ");
  return fallback;
}

const TABS = [
  { id: "fleet", labelKey: "decisions.fleet", icon: Plane },
  { id: "routes", labelKey: "decisions.route", icon: MapPin },
  { id: "pricing", labelKey: "decisions.fareStrategy", icon: DollarSign },
  { id: "marketing", labelKey: "decisions.marketing", icon: Megaphone },
  { id: "hr", labelKey: "decisions.hr", icon: Users },
  { id: "operations", labelKey: "decisions.maintenanceLabel", icon: Wrench },
  { id: "financing", labelKey: "decisions.financing", icon: Banknote },
  { id: "strategic", labelKey: "decisions.strategicDecisions", icon: Zap },
  { id: "experience", labelKey: "decisions.groundExperience", icon: Heart },
  { id: "network", labelKey: "decisions.scheduleOptimization", icon: Clock },
  { id: "risk", labelKey: "decisions.environmentalESG", icon: Shield },
  { id: "events", labelKey: "decisions.events", icon: AlertCircle },
  { id: "whatif", labelKey: "whatIf.title", icon: SlidersHorizontal },
];

export default function DecisionsTab({ game, team, decision, setDecision, onRefresh, readOnly }) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [localDecision, setLocalDecision] = useState(decision || {});
  const [hasChanges, setHasChanges] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [preflight, setPreflight] = useState(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (decision) {
      setLocalDecision(decision);
    }
  }, [decision, setLocalDecision]);

  const updateDecision = (field, value) => {
    if (readOnly) return;
    setLocalDecision(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!team?.id || !game?.current_quarter) return;
    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/teams/${team.id}/decisions/${game.current_quarter}`,
        localDecision
      );
      setDecision(response.data);
      setHasChanges(false);
      toast.success(t("decisions.decisionsSaved"));
    } catch (error) {
      toast.error(parseApiError(error, t("decisions.failedSave")));
    } finally {
      setSaving(false);
    }
  };

  const openConfirm = async () => {
    if (!team?.id || !game?.current_quarter) return;
    setConfirmOpen(true);
    setChecking(true);
    setPreflight(null);
    try {
      // Persist current edits so the preflight check reflects what will be submitted
      const saved = await axios.put(
        `${API}/teams/${team.id}/decisions/${game.current_quarter}`,
        localDecision
      );
      setDecision(saved.data);
      setHasChanges(false);
      const res = await axios.get(`${API}/teams/${team.id}/decisions/${game.current_quarter}/preflight`);
      setPreflight(res.data);
    } catch (error) {
      // Non-blocking: allow submit even if the check fails
      setPreflight(null);
    } finally {
      setChecking(false);
    }
  };

  const handleSubmit = async () => {
    if (!team?.id || !game?.current_quarter) return;
    setSubmitting(true);
    try {
      await axios.put(
        `${API}/teams/${team.id}/decisions/${game.current_quarter}`,
        localDecision
      );
      await axios.post(`${API}/teams/${team.id}/decisions/${game.current_quarter}/submit`);
      toast.success(t("decisions.decisionsSubmittedQuarter"));
      setConfirmOpen(false);
      onRefresh();
    } catch (error) {
      toast.error(parseApiError(error, t("decisions.failedSubmit")));
    } finally {
      setSubmitting(false);
    }
  };

  if (decision?.submitted) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-heading font-bold text-slate-900" data-testid="decisions-heading">{t("nav.decisions")}</h2>
          <p className="text-slate-500">{t("history.quarter")} {game?.current_quarter}</p>
        </div>
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="py-8 text-center">
            <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-emerald-800 mb-2" data-testid="decisions-submitted-msg">{t("decisions.alreadySubmitted")}</h3>
            <p className="text-emerald-600">{t("decisions.decisionsSubmitted")}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-heading font-bold text-slate-900" data-testid="decisions-heading">{t("nav.decisions")}</h2>
          <p className="text-slate-500">{t("history.quarter")} {game?.current_quarter} - {t("decisions.subtitle")}</p>
        </div>
        <div className="flex gap-3">
          {!readOnly && (
            <>
              <Button variant="outline" onClick={handleSave} disabled={saving || !hasChanges} className="gap-2" data-testid="save-decisions-btn">
                <Save className="w-4 h-4" />
                {saving ? t("common.loading") : t("common.save")}
              </Button>
              <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <Button disabled={submitting} onClick={openConfirm} className="gap-2 bg-emerald-600 hover:bg-emerald-700" data-testid="submit-decisions-btn">
                  <Send className="w-4 h-4" />
                  {submitting ? t("common.loading") : t("decisions.submitDecisions")}
                </Button>
                <AlertDialogContent data-testid="submit-confirm-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>{preflight?.has_warnings ? t("decisions.submitWarningsTitle") : t("decisions.confirmSubmitTitle")}</AlertDialogTitle>
                    <AlertDialogDescription>{t("decisions.confirmSubmitMessage")}</AlertDialogDescription>
                  </AlertDialogHeader>

                  {checking && (
                    <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="preflight-checking">
                      <span className="w-4 h-4 border-2 border-slate-300 border-t-slate-500 rounded-full animate-spin" />
                      {t("common.loading")}
                    </div>
                  )}

                  {!checking && preflight?.has_warnings && (
                    <div className="space-y-2" data-testid="preflight-warnings">
                      {(preflight.staffing.gap.pilots + preflight.staffing.gap.cabin_crew + preflight.staffing.gap.ground_staff) > 0 && (
                        <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-xs text-rose-800" data-testid="preflight-staffing-warning">
                          <Users className="w-4 h-4 shrink-0 mt-0.5 text-rose-500" />
                          <span>{t("decisions.submitDeficit", { pilots: preflight.staffing.gap.pilots, cabin: preflight.staffing.gap.cabin_crew, ground: preflight.staffing.gap.ground_staff })}</span>
                        </div>
                      )}
                      {preflight.routes_without_aircraft.length > 0 && (
                        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800" data-testid="preflight-routes-warning">
                          <MapPin className="w-4 h-4 shrink-0 mt-0.5 text-amber-500" />
                          <span>{t("decisions.submitRoutesNoAircraft", { routes: preflight.routes_without_aircraft.map(r => r.name).join(", ") })}</span>
                        </div>
                      )}
                    </div>
                  )}

                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="submit-cancel-btn">{t("common.cancel")}</AlertDialogCancel>
                    <AlertDialogAction onClick={handleSubmit} disabled={submitting || checking} className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-confirm-btn">
                      {preflight?.has_warnings ? t("decisions.submitAnyway") : t("decisions.submitDecisions")}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
        </div>
      </div>

      {hasChanges && (
        <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-4 py-2 rounded-lg" data-testid="unsaved-changes-banner">
          <AlertCircle className="w-4 h-4" />
          <span className="text-sm">{t("decisions.unsavedChanges")}</span>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="fleet" className="space-y-6">
        <TabsList className="bg-slate-100 p-1 flex-wrap h-auto gap-1">
          {TABS.map(tab => (
            <TabsTrigger key={tab.id} value={tab.id} className="gap-1.5 text-xs sm:text-sm" data-testid={`tab-${tab.id}`}>
              <tab.icon className="w-3.5 h-3.5" />
              <span className="sr-only sm:not-sr-only">{t(tab.labelKey)}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="fleet">
          <FleetSection decision={localDecision} updateDecision={updateDecision} team={team} />
        </TabsContent>
        <TabsContent value="routes">
          <RoutesSection decision={localDecision} updateDecision={updateDecision} team={team} />
        </TabsContent>
        <TabsContent value="pricing">
          <PricingSection decision={localDecision} updateDecision={updateDecision} team={team} />
        </TabsContent>
        <TabsContent value="marketing">
          <MarketingSection decision={localDecision} updateDecision={updateDecision} />
        </TabsContent>
        <TabsContent value="hr">
          <HRSection decision={localDecision} updateDecision={updateDecision} team={team} />
        </TabsContent>
        <TabsContent value="operations">
          <OperationsSection decision={localDecision} updateDecision={updateDecision} team={team} />
        </TabsContent>
        <TabsContent value="financing">
          <FinancingSection decision={localDecision} updateDecision={updateDecision} team={team} />
        </TabsContent>
        <TabsContent value="strategic">
          <StrategicSection decision={localDecision} updateDecision={updateDecision} team={team} game={game} />
        </TabsContent>
        <TabsContent value="experience">
          <CustomerExperienceSection decision={localDecision} updateDecision={updateDecision} />
        </TabsContent>
        <TabsContent value="network">
          <NetworkSection decision={localDecision} updateDecision={updateDecision} team={team} />
        </TabsContent>
        <TabsContent value="risk">
          <RiskSection decision={localDecision} updateDecision={updateDecision} />
        </TabsContent>
        <TabsContent value="events">
          <EventsSection game={game} team={team} onRefresh={onRefresh} />
        </TabsContent>
        <TabsContent value="whatif">
          <WhatIfSimulator team={team} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
